import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../auth/useIdentity";
import { getContactDetails, type ContactDetails } from "../api/account";

export const SECURITY_PATH = "/settings/security";
const SHOWN_KEY = "ankhos.accountReminderShown";

/** What the reminder asks for; empty = nothing to remind. */
export function accountReminders(d: ContactDetails): string[] {
  const out: string[] = [];
  if (!d.mobile) out.push("Add your mobile number: every AnkhOS login needs one.");
  else if (!d.mobileVerified) out.push("Verify your mobile number.");
  if (!d.email) out.push("Add an email address and verify it.");
  else if (!d.emailVerified) out.push("Verify your email address.");
  if (d.providers.length > 0 && d.links.length === 0) out.push("Link Google: the quickest way to sign in.");
  return out;
}

function shownThisSession(): boolean {
  try {
    return window.sessionStorage.getItem(SHOWN_KEY) === "1";
  } catch {
    return true;
  }
}

function markShown() {
  try {
    window.sessionStorage.setItem(SHOWN_KEY, "1");
  } catch {
    // nothing
  }
}

/**
 * After sign-in (owner 2026-10-25):
 *  - a login whose role needs a second factor and has no second way yet is
 *    kept on Settings › Sign-in & security until it adds one (owner: "Forced
 *    setup screen");
 *  - anyone whose contact details are not complete -- mobile or email not
 *    verified, Google not linked -- is reminded once per browser session.
 */
export function AccountReminder() {
  const identity = useIdentity();
  const ns = identity.rootNamespace ?? identity.namespace ?? "";
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const contact = useQuery({
    queryKey: ["contactDetails", ns],
    enabled: Boolean(identity.token && ns),
    retry: false,
    staleTime: 60_000,
    queryFn: ({ signal }) => getContactDetails(identity, ns, signal),
  });
  const [open, setOpen] = useState(false);
  const d = contact.data;
  const setup = d?.twoFactorSetupNeeded === true;
  const items = d ? accountReminders(d) : [];

  useEffect(() => {
    if (setup && pathname !== SECURITY_PATH) navigate(SECURITY_PATH, { replace: true });
  }, [setup, pathname, navigate]);

  useEffect(() => {
    if (!setup && items.length > 0 && !shownThisSession() && pathname !== SECURITY_PATH) {
      markShown();
      setOpen(true);
    }
  }, [setup, items.length, pathname]);

  if (setup) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }} data-testid="setup-required">
        Your role needs a second sign-in step. {d && !(d.mobile && d.mobileVerified)
          ? "Add and verify your mobile number below to continue using AnkhOS."
          : "Add an authenticator app or verify an email address below to continue using AnkhOS."}
      </Alert>
    );
  }
  return (
    <Dialog open={open} onClose={() => setOpen(false)} data-testid="account-reminder">
      <DialogTitle>Finish securing your account</DialogTitle>
      <DialogContent>
        {items.map((t) => (
          <Typography key={t} variant="body2" sx={{ mb: 1 }}>
            • {t}
          </Typography>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)}>Later</Button>
        <Button
          variant="contained"
          onClick={() => {
            setOpen(false);
            navigate(SECURITY_PATH);
          }}
        >
          Go to My contact details
        </Button>
      </DialogActions>
    </Dialog>
  );
}
